import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { formatPercent } from '@/lib/format'

export interface BarListItem {
    key: string
    label: string
    value: number
    detail?: string
}

interface BarListProps {
    items: BarListItem[]
    formatValue: (value: number) => string
    color?: string
    showShare?: boolean
    total?: number
    className?: string
}

// Horizontal magnitude bars with direct labels. Square at the baseline, rounded at the data end.
export const BarList: FC<BarListProps> = ({ items, formatValue, color = 'var(--chart-2)', showShare = false, total, className }): ReactNode => {
    const max = Math.max(...items.map((item) => item.value), 0)
    const sum = total ?? items.reduce((acc, item) => acc + item.value, 0)

    return (
        <ul className={cn('space-y-3.5', className)}>
            {items.map((item) => {
                const width = max === 0 ? 0 : (item.value / max) * 100
                const share = sum === 0 ? 0 : (item.value / sum) * 100

                return (
                    <li key={item.key} className='group'>
                        <div className='flex items-baseline justify-between gap-3 text-sm'>
                            <span className='min-w-0 truncate text-foreground'>
                                {item.label}
                                {item.detail && (
                                    <span className='ml-1.5 text-xs text-muted-foreground'>
                                        {item.detail}
                                    </span>
                                )}
                            </span>

                            <span className='shrink-0 font-medium text-foreground tabular-nums'>
                                {formatValue(item.value)}
                                {showShare && (
                                    <span className='ml-1.5 font-normal text-muted-foreground'>
                                        {formatPercent(share, share < 10 ? 1 : 0)}
                                    </span>
                                )}
                            </span>
                        </div>

                        <div className='mt-1.5 h-2 overflow-hidden rounded-r-[4px] bg-muted/80' aria-hidden='true'>
                            <div
                                className='h-full rounded-r-[4px] transition-[width,filter] duration-500 group-hover:brightness-110'
                                style={{ width: `${width}%`, background: color }}
                            />
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}
